import { AnimatePresence, MotionConfig, motion, useReducedMotion } from "framer-motion";
import { Component, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Link } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import YuxelGlobalFog from "./components/YuxelGlobalFog";
import "./yuxel-final-system.css";
import InteractionLayer from "./components/InteractionLayer";
import { initSmoothScroll, destroySmoothScroll, scrollTopImmediate } from "./lib/scroll";
import Home from "./pages/Home";
import ServicesIndex from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import WorkIndex from "./pages/Work";
import CaseStudy from "./pages/CaseStudy";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Insights from "./pages/Insights";
import InsightDetail from "./pages/InsightDetail";
import Admin from "./pages/Admin";
import Sector from "./pages/Sector";
import { WhatsAppButton } from "./components/WhatsAppButton";
import { useJsonLd, organizationLd, websiteLd } from "./lib/seo";
import { LangProvider, useLang } from "./i18n";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center bg-ink px-6 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">Something broke</p>
          <h1 className="mt-4 font-serif text-3xl text-white">This section failed to load</h1>
          <button
            type="button"
            data-testid="error-boundary-reload-button"
            onClick={() => window.location.reload()}
            className="mt-8 rounded-full bg-gold px-6 py-3 font-semibold text-ink"
          >
            Reload the page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    scrollTopImmediate();
  }, [pathname]);
  return null;
}

function SmoothScroll() {
  useEffect(() => {
    initSmoothScroll();
    return () => destroySmoothScroll();
  }, []);
  return null;
}

function GlobalHead() {
  useJsonLd("organization", organizationLd);
  useJsonLd("website", websiteLd);
  useEffect(() => {
    const id = process.env.REACT_APP_GTM_ID;
    if (!id || document.getElementById("gtm-script")) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    const s = document.createElement("script");
    s.id = "gtm-script";
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${id}`;
    document.head.appendChild(s);
  }, []);
  return null;
}

function NotFound() {
  const { t, href } = useLang();
  return (
    <div className="flex min-h-[70vh] flex-col items-center justify-center px-6 pt-20 text-center">
      <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">404</p>
      <h1 className="mt-4 font-serif text-3xl text-white sm:text-4xl">
        {t("This page doesn't exist — yet.")}
      </h1>
      <Link
        to={href("/")}
        data-testid="notfound-home-link"
        className="mt-8 rounded-full bg-gold px-6 py-3 font-semibold text-ink"
      >
        {t("Back to home")}
      </Link>
    </div>
  );
}

function AnimatedRoutes() {
  const location = useLocation();
  const reduce = useReducedMotion();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={location.pathname}
        initial={reduce ? false : { opacity: 0, clipPath: "inset(0 0 8% 0)", y: 18 }}
        animate={{ opacity: 1, clipPath: "inset(0 0 0% 0)", y: 0 }}
        transitionEnd={{ clipPath: "none", transform: "none" }}
        exit={reduce ? undefined : { opacity: 0, clipPath: "inset(0 0 10% 0)", y: -12 }}
        transition={{ duration: reduce ? 0 : 0.20, ease: "easeOut" }}
      >
        <Routes location={location}>
          <Route path="/" element={<Home />} />
          <Route path="/az" element={<Home />} />
          <Route path="/services" element={<ServicesIndex />} />
          <Route path="/xidmetler" element={<ServicesIndex />} />
          <Route path="/services/:slug" element={<ServiceDetail />} />
          <Route path="/xidmetler/:slug" element={<ServiceDetail />} />
          <Route path="/work" element={<WorkIndex />} />
          <Route path="/case-studies" element={<WorkIndex />} />
          <Route path="/work/:slug" element={<CaseStudy />} />
          <Route path="/case-studies/:slug" element={<CaseStudy />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/baxislar" element={<Insights />} />
          <Route path="/insights/:slug" element={<InsightDetail />} />
          <Route path="/baxislar/:slug" element={<InsightDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/haqqimizda" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/elaqe" element={<Contact />} />
          <Route path="/sectors/:slug" element={<Sector />} />
          <Route path="/saheler/:slug" element={<Sector />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function RouteExtras() {
  const { pathname } = useLocation();
  if (pathname === "/" || pathname === "/az") return null;
  return <><Footer /><WhatsAppButton /></>;
}

export default function App() {
  return (
    <div className="App yuxel-complete">
      <YuxelGlobalFog />
      <BrowserRouter>
        <LangProvider>
        <MotionConfig reducedMotion="user">
        <ErrorBoundary>
          <SmoothScroll />
          <ScrollToTop />
          <GlobalHead />
          <Navbar />
          <main id="main">
            <AnimatedRoutes />
          </main>
          <RouteExtras />

          <InteractionLayer />
          <Toaster position="bottom-right" />
        </ErrorBoundary>
        </MotionConfig>
        </LangProvider>
      </BrowserRouter>
    </div>
  );
}
